<script lang="ts">
	import dayjs from 'dayjs';
	import { twMerge } from 'tailwind-merge';
	import type { LiveDropInfoResponse } from '#lib/features/live-drop-info/types.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { PrimaryRoutes } from '#lib/websites/primary/constants/routes.js';
	import { page } from '$app/state';
	import { ArtworkCard } from '$global/components/ArtworkCard';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { GlobalHomePageData } from '$routes/types';

	interface Props {
		liveDropInfo: LiveDropInfoResponse | null;
		classes?: {
			card?: string;
		};
	}

	let { liveDropInfo, classes }: Props = $props();

	const dataCy = 'latest-releases';

	let data = $derived(page.data as GlobalHomePageData);

	let latestReleases = $derived(data.latestReleases.slice(0, 4));

	// `platform` is nullable in the REST contract (Drop_Config.platform is a
	// nullable column). A null falls through to the default branch and yields an
	// empty string.
	const getPlatformName = (platform: string | null) => {
		switch (platform) {
			case 'primary':
				return 'HENI Primary';
			case 'editions':
				return 'HENI Editions';
			default:
				return '';
		}
	};

	const getReleaseDate = (release: GlobalHomePageData['latestReleases'][0]) =>
		release.drop_start_date
			? dayjs(release.drop_start_date).format('D MMMM YYYY')
			: '';

	const getArtistName = (release: GlobalHomePageData['latestReleases'][0]) => {
		if (release.platform === 'primary') {
			return (
				Array.from(
					new Set(
						(release.products || []).flatMap((product) =>
							product?.primary_artworks?.[0]?.primary_collection?.artists?.map(
								(artist) => artist?.Artists_id?.name
							)
						)
					)
				)
					.filter(Boolean)
					.join(', ') || ''
			);
		}
		if (release.platform === 'editions') {
			return (
				Array.from(
					new Set(
						(release.products || []).flatMap((product) =>
							product?.editions?.[0]?.artists?.map(
								(artist) => artist?.Artists_id?.name
							)
						)
					)
				)
					.filter(Boolean)
					.join(', ') || ''
			);
		}

		return '';
	};

	const getHref = (release: GlobalHomePageData['latestReleases'][0]) => {
		if (release.platform === 'primary') {
			return `${PrimaryRoutes.Release}/${release.drop_page?.slug}`;
		}
		if (release.platform === 'editions') {
			return `${GlobalRoutes.EditionsHome}/${release.drop_page?.slug}`;
		}
		return '';
	};

	let getLiveStatus = (release: GlobalHomePageData['latestReleases'][0]) => {
		if (release.platform === 'primary') {
			const code =
				release.products?.[0]?.primary_artworks?.[0]?.primary_collection
					?.code || '';

			return liveDropInfo?.primary[code]?.isLive || false;
		}
		if (release.platform === 'editions') {
			const code = release.products?.[0]?.editions?.[0]?.code || '';
			return liveDropInfo?.editions[code]?.isLive || false;
		}

		return false;
	};

	const getImage = (release: GlobalHomePageData['latestReleases'][0]) => {
		if (release.platform === 'primary') {
			return release.products?.[0]?.primary_artworks?.[0]?.primary_collection
				?.image;
		}
		if (release.platform === 'editions') {
			if (release.products?.[0]?.editions?.[0]?.series?.featured_image) {
				return release.products?.[0]?.editions?.[0]?.series?.featured_image;
			}
			return release.products?.[0]?.editions?.[0]?.image;
		}

		return null;
	};
</script>

{#each latestReleases as release}
	{@const image = release.latest_releases_image || getImage(release)}
	{@const releaseName = release.name || ''}
	{@const releaseDate = getReleaseDate(release)}
	{@const isLive = getLiveStatus(release)}

	<ArtworkCard
		{dataCy}
		alt={releaseName}
		image={{
			url: getImageUrl(image?.filename_disk, '512px'),
			width: image?.width || 0,
			height: image?.height || 0,
		}}
		line1={getArtistName(release)}
		line2={releaseName}
		line3={`${getPlatformName(release.platform)}${releaseDate ? ` · ${releaseDate}` : ''}`}
		href={getHref(release)}
		classes={{
			container: twMerge(
				'border border-gray-200 rounded overflow-hidden',
				classes?.card
			),
			imageContainer: 'bg-gray-75 h-[15rem] xs:h-[18.75rem]',
			info: 'h-[calc(100%-15rem)] xs-[calc(100%-18.75rem)]',
			image: 'p-[.5rem] h-[15rem] xs:h-[18.75rem]',
			card: 'h-full',
			line1: 'mb-1',
			line2: ' mb-1',
			line3: 'font-normal mb-2',
		}}
	>
		{#if isLive}
			<div class="mt-3 flex" data-cy={`${dataCy}-live-now`}>
				<span
					class="flex w-auto items-center gap-1 rounded-full border border-gray-200 p-1 px-1.5"
				>
					<span class="h-[8px] w-[8px] rounded-full bg-[#22C55E]"></span>
					<Txt variant="body3">Live</Txt>
				</span>
			</div>
		{/if}
	</ArtworkCard>
{/each}
